import React, { useEffect, useState } from 'react'
import Layout from './Layout'
import Tooltip from '../shared/Tooltip'
import Alert from '../shared/Alert'

import { formatDollars } from '../../other/money'

import Filters from './accounts/Filters'
import AccountsTable from './accounts/AccountsTable'

const FILTERS_NOT_IN_URL_BAR = ['columns']
const DEFAULT_SHOWN_COLUMNS = [
  'Name',
  'Reference',
  'File Number',
  'Amount',
  'Status',
  'Status Date',
  'Offer Amount',
  '# Pays',
  '# Tiers',
  '%',
  'First Payment',
]

const Accounts = () => {
  const [basicStats, setBasicStats] = useState(null)
  const [accountsData, setAccountsData] = useState(null)
  const [scope] = useState(null)
  const [numPages, setNumPages] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search)
    return parseInt(urlParams.get('num_pages')) || 1
  })
  const [autoAction] = useState(() => {
    // const urlParams = new URLSearchParams(window.location.search);
    // return urlParams.get('auto_action') || null;
    return null
  })
  const [collectorMetrics, setCollectorMetrics] = useState(null)

  const [selectedFilterValues, setSelectedFilterValues] = useState(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const initialValues = {
      columns: DEFAULT_SHOWN_COLUMNS, // Set default columns
    }

    // First check for search parameter specifically
    const searchValue = urlParams.get('filters[search]')
    if (searchValue) {
      initialValues.search = searchValue
    }

    // Check for customer_document_id specifically
    const customerDocumentId = urlParams.get('filters[customer_document_id]')
    if (customerDocumentId) {
      initialValues.customer_document_id = customerDocumentId
    }

    // Extract all other filter parameters from URL
    for (const [key, value] of urlParams.entries()) {
      if (key.startsWith('filters[') && key.endsWith(']')) {
        const matches = key.match(/filters\[([^\]]+)\]\[([^\]]+)\]/)
        if (!matches) {
          // Handle simple filter parameters that don't have nested structure
          const simpleMatch = key.match(/filters\[([^\]]+)\]/)
          if (simpleMatch && !['search', 'customer_document_id'].includes(simpleMatch[1])) {
            initialValues[simpleMatch[1]] = value
          }
          continue
        }

        const [, filterName, arrayIndexOrKey] = matches

        if (['search', 'customer_document_id'].includes(filterName)) continue

        if (!initialValues[filterName]) {
          initialValues[filterName] = Number.isInteger(parseInt(arrayIndexOrKey)) ? [] : {}
        }

        if (Number.isInteger(parseInt(arrayIndexOrKey))) {
          if (Array.isArray(initialValues[filterName])) {
            initialValues[filterName].push(value)
          }
        } else {
          if (!Array.isArray(initialValues[filterName])) {
            initialValues[filterName][arrayIndexOrKey] = value
          }
        }
      }
    }

    return initialValues
  })

  useEffect(() => {
    const fetchCollectorMetrics = async () => {
      const collectorMetricsResponse = await fetch(`/collectors/api/calculate_collector_metrics`, {
        headers: {
          Accept: 'application/json',
        },
      })
      const collectorMetricsJson = await collectorMetricsResponse.json()
      setCollectorMetrics(collectorMetricsJson)
    }
    fetchCollectorMetrics()
  }, [])

  const [filters, setFilters] = useState([])

  useEffect(() => {
    const urlParams = new URLSearchParams()
    urlParams.set('num_pages', numPages)

    Object.entries(selectedFilterValues)
      .filter(([key]) => key !== 'columns') // Exclude columns from URL parameters
      .forEach(([key, value]) => {
        if (typeof value === 'object') {
          // Handle range filters
          Object.entries(value).forEach(([rangeKey, rangeValue]) => {
            urlParams.set(`filters[${key}][${rangeKey}]`, rangeValue)
          })
        } else {
          // Handle regular filters
          urlParams.set(`filters[${key}]`, value)
        }
      })

    const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`
    window.history.pushState({}, '', newUrl)
  }, [selectedFilterValues, numPages])

  const fetchAccountData = async () => {
    const urlParams = new URLSearchParams()
    urlParams.set('num_pages', numPages)

    const filtersString = Object.entries(selectedFilterValues)
      .filter(([key]) => !FILTERS_NOT_IN_URL_BAR.includes(key))
      .map(([key, value]) => {
        if (typeof value === 'object') {
          // Handle range filters
          return Object.entries(value)
            .map(([rangeKey, rangeValue]) => `filters[${key}][${rangeKey}]=${rangeValue}`)
            .join('&')
        }
        // Handle regular filters
        return `filters[${key}]=${value}`
      })
      .join('&')

    const accountsDataResponse = await fetch(
      `/collectors/api/apply_filters?${urlParams.toString()}&${filtersString}`,
      {
        headers: {
          Accept: 'application/json',
        },
      }
    )
    const accountsDataJson = await accountsDataResponse.json()
    setAccountsData(accountsDataJson)
  }

  const fetchFilterValues = async () => {
    const filterValuesResponse = await fetch(`/collectors/api/filter_values`, {
      headers: {
        Accept: 'application/json',
      },
    })
    const filterValuesJson = await filterValuesResponse.json()
    setFilters(filterValuesJson.filters)
  }

  const fetchBasicStats = async () => {
    const basicStatsResponse = await fetch(`/collectors/api/basic_stats`, {
      headers: {
        Accept: 'application/json',
      },
    })
    const basicStatsJson = await basicStatsResponse.json()
    setBasicStats(basicStatsJson)
  }

  useEffect(() => {
    setAccountsData(null)
    fetchAccountData()
  }, [numPages, selectedFilterValues])

  useEffect(() => {
    fetchBasicStats()
    fetchFilterValues()
  }, [])

  const handleFilterChange = (key, value, rangeKey = null) => {
    const clonedFilterValues = JSON.parse(JSON.stringify(selectedFilterValues))
    if (rangeKey) {
      // Handle range filters
      clonedFilterValues[key] = clonedFilterValues[key] || {}
      clonedFilterValues[key][rangeKey] = value
    } else {
      // Handle regular filters
      clonedFilterValues[key] = value
    }
    setSelectedFilterValues(clonedFilterValues)
  }

  const handleFilterRemove = key => {
    const clonedFilterValues = { ...selectedFilterValues }
    delete clonedFilterValues[key]
    setSelectedFilterValues(clonedFilterValues)
  }

  const handleClearAll = () => {
    window.location.href = '/collectors/accounts'
  }

  return (
    <Layout scope={scope}>
      <div>
        {basicStats && (
          <>
            {basicStats?.is_on_call_admin && (
              <Alert type='primary' className='mb-4'>
                Viewing user accounts as administrator.
              </Alert>
            )}

            {basicStats?.law_firm && (
              <>
                <h2 className='font-serif display-4 mb-4'>Hello, {basicStats.law_firm.name}</h2>

                <p className='lead text-muted mb-0'>
                  Consumers are notified that each communication is from a debt collector.
                </p>

                <br />

                {collectorMetrics === null ? (
                  <div className='placeholder-glow mb-5'>
                    <span className='placeholder col-12' style={{ height: '120px' }}></span>
                  </div>
                ) : (
                  <div className='row row-cols-auto g-3 mb-5'>
                    <div className='col'>
                      <div className='card'>
                        <div className='card-body px-6 py-4 text-nowrap'>
                          <Tooltip
                            text='The amount of money consumers have paid to your organization'
                            position='top'
                          >
                            <h6 className='mb-1'>Collected</h6>
                            <h4 className='display-4 text-success mb-0'>
                              {formatDollars(collectorMetrics.collected_amount || 0, false)}
                            </h4>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                    <div className='col'>
                      <div className='card'>
                        <div className='card-body px-6 py-4 text-nowrap'>
                          <Tooltip
                            text='The sum of debt amounts on SoloSettle for your organization'
                            position='top'
                          >
                            <h6 className='mb-1'>Uncollected</h6>
                            <h4 className='display-4 text-danger mb-0'>
                              {formatDollars(collectorMetrics.uncollected_amount || 0, false)}
                            </h4>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                    <div className='col'>
                      <div className='card'>
                        <div className='card-body px-6 py-4 text-nowrap'>
                          <Tooltip
                            text='The sum of debt amounts for accounts awaiting the consumers input'
                            position='top'
                          >
                            <h6 className='mb-1'>Waiting on consumer</h6>
                            <h4 className='display-4 text-success mb-0'>
                              {formatDollars(
                                collectorMetrics.waiting_on_consumer_amount || 0,
                                false
                              )}
                            </h4>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                    <div className='col'>
                      <div className='card'>
                        <div className='card-body px-6 py-4 text-nowrap'>
                          <Tooltip
                            text='The sum of debt amounts for accounts awaiting your input'
                            position='top'
                          >
                            <h6 className='mb-1'>Waiting on you</h6>
                            <h4 className='display-4 text-danger mb-0'>
                              {formatDollars(
                                collectorMetrics.waiting_on_collector_amount || 0,
                                false
                              )}
                            </h4>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                    <div className='col'>
                      <div className='card'>
                        <div className='card-body px-6 py-4 text-nowrap'>
                          <Tooltip
                            text='The number of accounts your organization has engaged with divided by total accounts'
                            position='top'
                          >
                            <h6 className='mb-1'>Your response rate</h6>
                            <h4
                              className={`display-4 mb-0 ${collectorMetrics.response_rate >= 90 ? 'text-success' : 'text-warning'}`}
                            >
                              {collectorMetrics.response_rate || 0}%
                            </h4>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                    <div className='col'>
                      <div className='card'>
                        <div className='card-body px-6 py-4 text-nowrap'>
                          <Tooltip
                            text='The number of first offers in the last 30 days'
                            position='top'
                          >
                            <h6 className='mb-1'>Recent offers</h6>
                            <h4 className='display-4 text-success mb-0'>
                              +{collectorMetrics.offers_count || 0}
                            </h4>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}

            <h1 className='display-3 mb-6'>Accounts</h1>

            {!filters || filters.length === 0 ? (
              <div className='placeholder-glow mb-5'>
                <span className='placeholder col-12' style={{ height: '60px' }}></span>
              </div>
            ) : (
              <Filters
                filters={filters}
                filterValues={selectedFilterValues}
                setFilter={handleFilterChange}
                removeFilter={handleFilterRemove}
                clearAll={handleClearAll}
              />
            )}
          </>
        )}

        {basicStats && !accountsData && (
          <div className='placeholder-glow mb-5'>
            {/* Table header placeholder */}
            <div className='mb-3'>
              <span className='placeholder col-1 me-2'></span>
              <span className='placeholder col-2 me-2'></span>
              <span className='placeholder col-2 me-2'></span>
              <span className='placeholder col-1 me-2'></span>
              <span className='placeholder col-2'></span>
            </div>
            {/* Table rows placeholders */}
            {[...Array(6)].map((_, i) => (
              <div key={i} className='mb-3'>
                <span className='placeholder col-1 me-2'></span>
                <span className='placeholder col-2 me-2'></span>
                <span className='placeholder col-2 me-2'></span>
                <span className='placeholder col-1 me-2'></span>
                <span className='placeholder col-2'></span>
              </div>
            ))}
          </div>
        )}

        {selectedFilterValues.customer_document_id && (
          <Alert type='primary'>
            Currently viewing a single account. Click{' '}
            <a href='#' onClick={() => handleFilterRemove('customer_document_id')}>
              here
            </a>{' '}
            to view all of them.
          </Alert>
        )}

        {basicStats && accountsData && (
          <>
            <AccountsTable
              lawFirm={basicStats.law_firm}
              selectedFilterValues={selectedFilterValues}
              accounts={accountsData.customer_info || []}
              autoAction={autoAction}
              setAccounts={accounts => {
                const clonedAccounts = JSON.parse(JSON.stringify(accountsData))
                clonedAccounts.customer_info = accounts
                setAccountsData(clonedAccounts)
              }}
              hasPaymentDetails={accountsData.has_payment_details}
              validFirstPaymentDate={accountsData.valid_first_payment_date}
            />

            {accountsData.more_pages && (
              <div className='text-center mt-4'>
                <button onClick={() => setNumPages(prev => prev + 1)} className='btn btn-primary'>
                  Load More
                </button>
              </div>
            )}

            <div className='mt-4 mb-4'>
              Showing {accountsData.customer_info?.length || 0} out of{' '}
              {accountsData.total_count || 0} matching accounts
            </div>
          </>
        )}
      </div>
    </Layout>
  )
}

export default Accounts
